import { useQuery } from '@tanstack/react-query';
import { guestApiClient } from '../services/apiClient';
import { billingService } from '../services/billingService';
import { useHotelBranding } from './useHotelBranding';
import { BookingDetails, BillSummary, CheckInPaymentPolicy, PreCheckInStatus } from '../types';

/**
 * Everything a gd screen needs about the current stay in one place:
 * pre-check-in status (incl. the hotel's check-in payment policy), the live
 * bill, and hotel branding. Query keys match the legacy screens' keys so
 * cache invalidations (e.g. after a payment confirm) stay coherent.
 */
export function useGuestStay(booking: BookingDetails | null | undefined) {
  const { hotelInfo, logoSrc, hotelCode } = useHotelBranding(booking?.hotel?.code);

  const statusQuery = useQuery({
    queryKey: ['preCheckInStatus'],
    queryFn: async () => {
      const response = await guestApiClient.get<{ success: boolean; data: PreCheckInStatus }>(
        '/api/guest/pre-checkin/status/'
      );
      return response.data.data;
    },
    enabled: !!booking,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 2,
  });

  const billQuery = useQuery({
    queryKey: ['billSummary'],
    queryFn: async () => {
      const response = await billingService.getBillSummary();
      return response.data as BillSummary;
    },
    enabled: !!booking,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 2,
  });

  const status = statusQuery.data;
  const policy: CheckInPaymentPolicy | null = status?.payment_policy || null;
  const isCheckedIn = !!status?.is_checked_in;
  const checkinState = status?.checkin_state
    || (isCheckedIn ? 'checked_in' : status?.can_checkin_now ? 'ready' : 'upcoming');
  const currency = policy?.currency || booking?.hotel?.currency;

  return {
    hotelInfo,
    logoSrc,
    hotelCode,
    status,
    statusQuery,
    bill: billQuery.data,
    billQuery,
    policy,
    checkinState,
    isCheckedIn,
    currency,
    canOrderFood: !!status?.available_actions?.can_order_food,
    canRequestServices: !!status?.available_actions?.can_request_services,
  };
}
